/* useFocusTrap — simple WCAG-compliant modal focus manager.
 *
 * Call from any modal / overlay component:
 *   const trapRef = useFocusTrap(open, onClose);
 *   return open ? <div ref={trapRef} role="dialog" aria-modal="true">…</div> : null;
 *
 * Behaviour:
 *   • When `open` flips true: records the previously-focused element,
 *     moves focus to the first focusable control inside the container.
 *   • Traps Tab / Shift-Tab inside the container (loops to first/last).
 *   • Esc fires `onClose()` so every modal is dismissable from the
 *     keyboard without hunting for the X button.
 *   • When `open` flips false: restores focus to the recorded element so
 *     the cook ends up back where they came from.
 */
import { useCallback, useEffect, useRef } from "react";

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

export default function useFocusTrap(open, onClose) {
  const containerRef = useRef(null);
  const previouslyFocused = useRef(null);

  const refCb = useCallback((node) => {
    containerRef.current = node;
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    // 1 — remember who had focus so we can restore when the modal closes.
    previouslyFocused.current = typeof document !== "undefined" ? document.activeElement : null;

    // 2 — focus the first focusable element inside the modal (next tick so
    // the DOM has mounted + React has flushed).
    const id = requestAnimationFrame(() => {
      const container = containerRef.current;
      if (!container) return;
      const focusables = container.querySelectorAll(FOCUSABLE);
      const first = focusables[0];
      if (first && typeof first.focus === "function") {
        first.focus();
      } else if (typeof container.focus === "function") {
        container.focus();
      }
    });

    // 3 — key handler: trap Tab + handle Esc.
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose?.();
        return;
      }
      if (e.key !== "Tab") return;
      const container = containerRef.current;
      if (!container) return;
      const focusables = Array.from(container.querySelectorAll(FOCUSABLE));
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);

    return () => {
      cancelAnimationFrame(id);
      document.removeEventListener("keydown", onKeyDown);
      // 4 — restore focus to the previously-focused element.
      const prev = previouslyFocused.current;
      if (prev && typeof prev.focus === "function") {
        try { prev.focus(); } catch { /* detached node — ignore */ }
      }
    };
  }, [open, onClose]);

  return refCb;
}
